import { createMachine, assign } from "xstate";

import { Availability, AvailabilityInput } from "../../apiHooks";

type Event =
    | {
          type: "AVAILABILITY_ID_UPDATE";
          availabilityId: string;
      }
    | {
          type: "AVAILABILITY_UPDATE";
          availability: Availability;
      }
    | {
          type: "AVAILABILITY_ERROR";
          error: Error;
      }
    | {
          type: "EDIT_OPTIONS";
      }
    | {
          type: "SET_OPTION";
          id: string;
          value: string;
      }
    | {
          type: "SET_PRICING_CATEGORY_UNITS";
          id: string;
          units: number;
      }
    | {
          type: "START_BOOKING";
      }
    | {
          type: "ERROR_BOOKING";
          error: Error;
      };

type Context = {
    availabilityId: string;
    saleCurrency?: string;
    availability: Availability | null;
    initialAvailability?: Availability | null;
    availabilityInput: AvailabilityInput;
    availabilityError: Error | null;
    bookingError: Error | null;
};

export const availabilityFormMachine = createMachine(
    {
        schema: {
            context: {} as Context,
            events: {} as Event,
        },
        tsTypes: {} as import("./availabilityForm.machine.typegen").Typegen0,
        context: {
            availabilityId: "",
            saleCurrency: undefined,
            availability: null,
            initialAvailability: null,
            availabilityInput: {
                optionList: [],
                pricingCategoryList: [],
            },
            availabilityError: null,
            bookingError: null,
        },
        id: "availabilityFormMachine",
        initial: "loading",
        on: {
            AVAILABILITY_ID_UPDATE: {
                actions: ["assignAvailabilityId", "resetInput"],
                target: ".loading",
            },
        },
        states: {
            loading: {
                tags: ["loading"],
                on: {
                    AVAILABILITY_UPDATE: {
                        actions: "availabilityUpdate",
                        target: "options.idle",
                    },
                    AVAILABILITY_ERROR: {
                        actions: "assignAvailabilityError",
                        target: "error",
                    },
                },
            },
            options: {
                on: {
                    AVAILABILITY_UPDATE: {
                        actions: "availabilityUpdate",
                        target: ".idle",
                    },
                    START_BOOKING: {
                        target: "booking",
                    },
                    SET_OPTION: {
                        actions: ["setOption", "clearPricingCategoryList", "clearBookingError"],
                        target: ".loadingOptions",
                    },
                    SET_PRICING_CATEGORY_UNITS: {
                        actions: ["setPricingCategoryUnits", "clearBookingError"],
                        target: ".debouncing",
                        internal: false,
                    },
                },
                states: {
                    idle: {
                        tags: ["pricingCategoriesVisible"],
                    },
                    debouncing: {
                        tags: ["pricingCategoriesVisible", "debouncing"],
                        after: {
                            500: { target: "loadingPricingCategories" },
                        },
                    },
                    loadingOptions: {
                        tags: ["loading"],
                    },
                    loadingPricingCategories: {
                        tags: ["loading", "pricingCategoriesVisible"],
                    },
                },
            },
            booking: {
                on: {
                    ERROR_BOOKING: {
                        actions: "assignBookingError",
                        target: "options.idle",
                    },
                },
                tags: ["pricingCategoriesVisible"],
            },
            error: {},
        },
    },
    {
        actions: {
            assignAvailabilityId: assign({
                availabilityId: (_, e) => e.availabilityId,
            }),
            assignAvailabilityError: assign({
                availabilityError: (_, e) => e.error,
            }),
            availabilityUpdate: assign({
                availability: (_, e) => e.availability,
                availabilityInput: (ctx, e) => {
                    const initialAvailabilityOptionList = ctx.initialAvailability?.optionList.nodes;
                    const newAvailabilityOptionList = e.availability.optionList.nodes;
                    const newAvailabilityOptionIds = newAvailabilityOptionList
                        .filter((option) => !!option.answerValue)
                        .map((option) => option.id);
                    const useInitialAvailabilityOptions = !ctx.availability && !!initialAvailabilityOptionList;
                    const optionListNodes = useInitialAvailabilityOptions
                        ? initialAvailabilityOptionList
                        : newAvailabilityOptionList;

                    const initialAvailabilityPricingCategoryList = ctx.initialAvailability?.pricingCategoryList?.nodes;
                    const newAvailabilityPricingCategoryList = e.availability.pricingCategoryList?.nodes;
                    const pricingCategoryList =
                        (useInitialAvailabilityOptions
                            ? initialAvailabilityPricingCategoryList
                            : newAvailabilityPricingCategoryList) ?? [];

                    return {
                        optionList: optionListNodes
                            .filter((option) => !!option.answerValue)
                            .map((option, index) => ({
                                id: useInitialAvailabilityOptions ? newAvailabilityOptionIds[index] : option.id,
                                value: option.answerValue!,
                            })),
                        pricingCategoryList: pricingCategoryList
                            .filter((pricingCategory) => pricingCategory.units > 0)
                            .map((pricingCategory) => ({
                                id: pricingCategory.id,
                                units: pricingCategory.units,
                            })),
                    };
                },
            }),
            resetInput: assign({
                availabilityInput: (_) => ({
                    optionList: [],
                    pricingCategoryList: [],
                }),
                availability: (_) => null,
            }),
            setOption: assign({
                availabilityInput: (ctx, e) => {
                    const optionList = [...ctx.availabilityInput.optionList];
                    const idx = optionList.findIndex((option) => option.id === e.id);
                    if (idx > -1) {
                        optionList[idx] = {
                            ...optionList[idx],
                            value: e.value,
                        };
                        return {
                            ...ctx.availabilityInput,
                            optionList,
                        };
                    }

                    return {
                        ...ctx.availabilityInput,
                        optionList: [...optionList, { id: e.id, value: e.value }],
                    };
                },
            }),
            setPricingCategoryUnits: assign({
                availabilityInput: (ctx, e) => {
                    const newPricingCategory = { id: e.id, units: e.units };
                    const pricingCategoryList = ctx.availabilityInput.pricingCategoryList;

                    if (pricingCategoryList.find((pricingCategory) => pricingCategory.id === newPricingCategory.id)) {
                        // if it already exist, update it
                        return {
                            ...ctx.availabilityInput,
                            pricingCategoryList: ctx.availabilityInput.pricingCategoryList.map((pricingCategory) => {
                                return newPricingCategory.id === pricingCategory.id
                                    ? newPricingCategory
                                    : pricingCategory;
                            }),
                        };
                    } else {
                        // otherwise, add it as a new pricing category
                        return {
                            ...ctx.availabilityInput,
                            pricingCategoryList: [...pricingCategoryList, newPricingCategory],
                        };
                    }
                },
            }),
            clearPricingCategoryList: assign({
                availabilityInput: (ctx) => ({
                    ...ctx.availabilityInput,
                    pricingCategoryList: [],
                }),
            }),
            assignBookingError: assign({
                bookingError: (_, e) => e.error,
            }),
            clearBookingError: assign({
                bookingError: (_) => null,
            }),
        },
    }
);
